import { Megaphone, MessageCircle, Search, Settings, Star, User } from 'lucide-react';
import { Link, NavLink, useLocation, useSearchParams } from 'react-router-dom';

import { Avatar } from './Avatar';
import { chatFilterFrom } from '@/features/chats/ChatListPanel';
import { useUiStore } from '@liniya/client-core';

interface Props {
  displayName: string;
  avatarUrl?: string | null;
  unreadTotal: number;
}

/**
 * The narrow navigation column of the desktop layout.
 *
 * It disappears entirely below 680px, where a bottom bar takes over: a 76px
 * vertical strip on a phone spends scarce width on navigation that a thumb
 * cannot comfortably reach anyway.
 */
export function Rail({ displayName, avatarUrl, unreadTotal }: Props) {
  const setSearchOpen = useUiStore((state) => state.setSearchOpen);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // NavLink decides "active" from the path alone, so every rail entry pointing
  // at /chats lit up at once. The filter is what actually differs between them.
  const onChats = location.pathname.startsWith('/chats');
  const filter = onChats ? chatFilterFrom(searchParams) : null;
  // Plain links, not NavLink: NavLink would also stamp aria-current on all
  // three, telling a screen reader the same lie the highlight told.
  const rail = (active: boolean) => ({
    className: active ? 'nav-button active' : 'nav-button',
    'aria-current': active ? ('page' as const) : undefined,
  });

  return (
    <aside className="rail">
      <NavLink className="brand" to="/chats" aria-label="Линия">
        <span className="line-logo" aria-hidden="true">
          <i />
        </span>
      </NavLink>

      <nav aria-label="Основная навигация">
        <Link {...rail(filter === 'all')} to="/chats" aria-label="Чаты" title="Чаты">
          <MessageCircle size={20} />
          {unreadTotal > 0 ? (
            <span className="badge" aria-label={`${unreadTotal} непрочитанных`}>
              {unreadTotal > 99 ? '99+' : unreadTotal}
            </span>
          ) : null}
        </Link>
        <button
          className="nav-button"
          type="button"
          aria-label="Поиск"
          title="Поиск (Ctrl K)"
          onClick={() => setSearchOpen(true)}
        >
          <Search size={20} />
        </button>
        <Link
          {...rail(filter === 'pinned')}
          to="/chats?filter=pinned"
          aria-label="Закреплённые"
          title="Закреплённые"
        >
          <Star size={20} />
        </Link>
        <Link
          {...rail(filter === 'channel')}
          to="/chats?filter=channel"
          aria-label="Каналы"
          title="Каналы"
        >
          <Megaphone size={20} />
        </Link>
      </nav>

      <div className="rail-foot">
        <NavLink
          className={({ isActive }) => (isActive ? 'nav-button active' : 'nav-button')}
          to="/settings"
          aria-label="Настройки"
          title="Настройки"
        >
          <Settings size={20} />
        </NavLink>
        <NavLink to="/profile" aria-label="Профиль" title="Профиль">
          <Avatar name={displayName} size="small" imageUrl={avatarUrl} />
        </NavLink>
      </div>
    </aside>
  );
}

/** The phone counterpart of the rail. */
export function MobileNav({ unreadTotal }: { unreadTotal: number }) {
  return (
    <nav className="mobile-nav" aria-label="Основная навигация">
      <NavLink to="/chats" className={({ isActive }) => (isActive ? 'active' : undefined)}>
        <MessageCircle size={20} />
        <span>Чаты</span>
        {unreadTotal > 0 ? <span className="badge">{unreadTotal > 99 ? '99+' : unreadTotal}</span> : null}
      </NavLink>
      <NavLink to="/settings" className={({ isActive }) => (isActive ? 'active' : undefined)}>
        <Settings size={20} />
        <span>Настройки</span>
      </NavLink>
      <NavLink to="/profile" className={({ isActive }) => (isActive ? 'active' : undefined)}>
        <User size={20} />
        <span>Профиль</span>
      </NavLink>
    </nav>
  );
}
